/**
 * SPEC-20260507-1400 (Fase 4): seletor de provider + model pra chamadas IA.
 *
 * Carrega `/api/ai/providers` no mount; cacheia no module-level pra evitar
 * fetch repetido entre instâncias. Última escolha persistida em localStorage.
 *
 * Uso:
 * ```tsx
 * <AIProviderSelect value={opts} onChange={setOpts} />
 * ```
 */

import { useEffect, useState } from "react";
import { Sparkles, AlertCircle } from "lucide-react";
import { listAIProviders, type AIProvidersCatalog, type AIOptions, type AIProvider } from "../../services/ai";

const LS_KEY = "wos.ai.lastProviderModel";

let catalogCache: AIProvidersCatalog | null = null;
let catalogPromise: Promise<AIProvidersCatalog> | null = null;

function loadCatalog(): Promise<AIProvidersCatalog> {
  if (catalogCache) return Promise.resolve(catalogCache);
  if (!catalogPromise) {
    catalogPromise = listAIProviders().then((c) => {
      catalogCache = c;
      return c;
    });
  }
  return catalogPromise;
}

interface PersistedChoice {
  provider: AIProvider;
  model: string;
}

function readPersisted(): PersistedChoice | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedChoice;
    if (parsed.provider && parsed.model) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

function writePersisted(c: PersistedChoice): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(c));
  } catch {
    /* ignore */
  }
}

export interface AIProviderSelectProps {
  value: AIOptions;
  onChange: (next: AIOptions) => void;
  /** Compact = sem labels visíveis (só selects). Default false. */
  compact?: boolean;
}

export function AIProviderSelect({ value, onChange, compact }: AIProviderSelectProps) {
  const [catalog, setCatalog] = useState<AIProvidersCatalog | null>(catalogCache);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (!catalogCache) {
      loadCatalog()
        .then((c) => {
          if (!alive) return;
          setCatalog(c);
          // Auto-set defaults se ainda não há escolha
          if (!value.provider || !value.model) {
            const persisted = readPersisted();
            const initial = persisted ?? c.default;
            onChange({ ...value, provider: initial.provider, model: initial.model });
          }
        })
        .catch((e) => {
          if (alive) setErr(e instanceof Error ? e.message : "Falha ao carregar providers");
        });
    } else if (!value.provider || !value.model) {
      const persisted = readPersisted();
      const initial = persisted ?? catalogCache.default;
      onChange({ ...value, provider: initial.provider, model: initial.model });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (err) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-danger">
        <AlertCircle size={11} /> {err}
      </span>
    );
  }
  if (!catalog) {
    return <span className="text-xs text-muted-foreground">Carregando providers…</span>;
  }

  const selectedProvider = (value.provider ?? catalog.default.provider) as AIProvider;
  const providerEntry = catalog.providers.find((p) => p.provider === selectedProvider);
  const isUnavailable = providerEntry && (!providerEntry.implemented || !providerEntry.configured);
  const selectedModel = value.model ?? providerEntry?.suggestedModels[0]?.id ?? catalog.default.model;
  const selectedModelEntry = providerEntry?.suggestedModels.find((m) => m.id === selectedModel);

  function setProvider(provider: AIProvider) {
    const entry = catalog!.providers.find((p) => p.provider === provider);
    const firstModel = entry?.suggestedModels[0]?.id ?? selectedModel;
    const next: AIOptions = { ...value, provider, model: firstModel };
    writePersisted({ provider, model: firstModel });
    onChange(next);
  }

  function setModel(model: string) {
    const next: AIOptions = { ...value, provider: selectedProvider, model };
    writePersisted({ provider: selectedProvider, model });
    onChange(next);
  }

  return (
    <div className={compact ? "flex items-center gap-2" : "space-y-2"}>
      {!compact && (
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1">
          <Sparkles size={10} /> Provider IA
        </p>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={selectedProvider}
          onChange={(e) => setProvider(e.target.value as AIProvider)}
          className="px-2 py-1 text-xs border border-border rounded bg-bg font-mono-tight"
        >
          {catalog.providers.map((p) => {
            const ok = p.implemented && p.configured;
            return (
              <option key={p.provider} value={p.provider}>
                {p.provider}
                {!ok ? " (indisponível)" : ""}
              </option>
            );
          })}
        </select>
        <select
          value={selectedModel}
          onChange={(e) => setModel(e.target.value)}
          className="px-2 py-1 text-xs border border-border rounded bg-bg font-mono-tight max-w-[280px]"
        >
          {providerEntry?.suggestedModels.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label} {m.thinking ? "🧠" : ""}
            </option>
          ))}
          {/* Permite digitar modelo livre (free-text) — option "outro" */}
          {providerEntry &&
            !providerEntry.suggestedModels.some((m) => m.id === selectedModel) && (
              <option value={selectedModel}>{selectedModel} (custom)</option>
            )}
        </select>
        {isUnavailable && providerEntry?.note && (
          <span
            className="text-[10px] text-warning-soft-fg bg-warning-soft px-1.5 py-0.5 rounded"
            title={providerEntry.note}
          >
            ⚠ {providerEntry.note}
          </span>
        )}
        {selectedModelEntry?.thinking && (
          <span className="text-[10px] text-muted-foreground">
            (extended thinking suportado)
          </span>
        )}
      </div>
    </div>
  );
}
